import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import html2canvas from 'html2canvas';
import '../styles/nassar-theme.css';

const NotesModal = ({ 
    isOpen, 
    onClose, 
    notes: initialNotes, 
    fileNum, 
    fileId, 
    customerName,
    goodsDescription,
    containerNumbers,
    onNoteAdded 
}) => {
    const [newNoteText, setNewNoteText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [notes, setNotes] = useState(initialNotes || []);
    const [editingNote, setEditingNote] = useState(null);
    const [editText, setEditText] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const hasChanges = useRef(false);
    const notesContentRef = useRef(null);

    useEffect(() => {
        setNotes(initialNotes || []);
    }, [initialNotes]);

    const getUserData = () => {
        try {
            const token = localStorage.getItem('nassar_token');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return {
                    name: payload.name || 'مستخدم',
                    type: payload.type || 'Employee',
                    id: payload.id
                };
            }
        } catch (e) {
            console.error('Error parsing token:', e);
        }
        return { name: 'مستخدم', type: 'Employee', id: null };
    };

    const userData = getUserData();

    if (!isOpen) return null;

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const sortedNotes = notes ? [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];

    const handleAddNote = async () => {
        if (!newNoteText.trim()) {
            setError('الرجاء إدخال نص الملاحظة');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const response = await axiosInstance.post('/notes/', {
                clearance_file: fileId,
                text: newNoteText.trim(),
                by_user: userData.name
            });

            setNotes(prev => [...prev, response.data]);
            setNewNoteText('');
            hasChanges.current = true;
        } catch (err) {
            console.error('Error adding note:', err);
            setError('حدث خطأ أثناء إضافة الملاحظة');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditNote = async (noteId) => {
        if (!editText.trim()) return;

        try {
            await axiosInstance.put(`/notes/${noteId}/`, {
                text: editText.trim()
            });

            setNotes(prev => prev.map(note => 
                note.id === noteId ? { ...note, text: editText.trim() } : note
            ));
            setEditingNote(null);
            setEditText('');
            hasChanges.current = true;
        } catch (err) {
            console.error('Error editing note:', err);
            setError('حدث خطأ أثناء تعديل الملاحظة');
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await axiosInstance.delete(`/notes/${noteId}/`);

            setNotes(prev => prev.filter(note => note.id !== noteId));
            setDeleteConfirm(null);
            hasChanges.current = true;
        } catch (err) {
            console.error('Error deleting note:', err);
            setError('حدث خطأ أثناء حذف الملاحظة');
        }
    };

    const handleClose = () => {
        setNewNoteText('');
        setError('');
        setEditingNote(null);
        setEditText('');
        setDeleteConfirm(null);
        
        if (hasChanges.current && onNoteAdded) {
            hasChanges.current = false;
            onNoteAdded();
        }
        
        onClose();
    };

    const handleExportImage = async () => {
        if (!notesContentRef.current) return;
        
        setIsExporting(true);
        try {
            const canvas = await html2canvas(notesContentRef.current, {
                scale: 3,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false
            });
            
            const link = document.createElement('a');
            link.download = `ملاحظات_ملف_${fileNum || 'غير_محدد'}_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.png`;
            link.href = canvas.toDataURL('image/png', 1.0);
            link.click();
        } catch (err) {
            console.error('Error exporting image:', err);
            setError('حدث خطأ أثناء تصدير الصورة');
        } finally {
            setIsExporting(false);
        }
    };

    const primaryColor = '#03428d';
    const primaryLight = '#e8f0fa';

    return (
        <div 
            className="modal-overlay" 
            onClick={handleClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '1rem'
            }}
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#fff',
                    borderRadius: '16px',
                    width: '100%',
                    maxWidth: '700px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, #0056b3 100%)`,
                    padding: '1rem 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <h2 style={{
                        margin: 0,
                        color: '#fff',
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg style={{ width: '22px', height: '22px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                        </svg>
                        ملاحظات ملف التخليص
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button 
                            onClick={handleExportImage}
                            disabled={isExporting || sortedNotes.length === 0}
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem 0.75rem',
                                cursor: isExporting || sortedNotes.length === 0 ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.4rem',
                                color: '#fff',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                opacity: sortedNotes.length === 0 ? 0.5 : 1,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => { if (sortedNotes.length > 0) e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            title="تصدير كصورة"
                        >
                            {isExporting ? (
                                <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                            ) : (
                                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                            )}
                            <span>تصدير صورة</span>
                        </button>
                        <button 
                            onClick={handleClose}
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.5rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                        >
                            <svg style={{ width: '20px', height: '20px', color: '#fff' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{
                    padding: '1.5rem',
                    overflowY: 'auto',
                    flex: 1
                }}>
                    {/* Export Content Area */}
                    <div ref={notesContentRef} style={{ backgroundColor: '#fff' }}>
                        {/* File Info Header - for export */}
                        <div style={{
                            backgroundColor: primaryLight,
                            borderRadius: '12px',
                            padding: '1rem 1.25rem',
                            marginBottom: '1rem',
                            border: `2px solid ${primaryColor}`
                        }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '0.75rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <svg style={{ width: '18px', height: '18px', color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                    </svg>
                                    <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>رقم الملف:</span>
                                    <span style={{ fontWeight: '700', color: primaryColor }}>{fileNum || 'غير محدد'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <svg style={{ width: '18px', height: '18px', color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                    </svg>
                                    <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>الزبون:</span>
                                    <span style={{ fontWeight: '700', color: primaryColor }}>{customerName || 'غير محدد'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <svg style={{ width: '18px', height: '18px', color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>البضاعة:</span>
                                    <span style={{ fontWeight: '700', color: primaryColor }}>{goodsDescription || 'غير محدد'}</span>
                                </div>
                                {containerNumbers && containerNumbers.length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: containerNumbers.length > 1 ? '1 / -1' : 'auto' }}>
                                        <svg style={{ width: '18px', height: '18px', color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" />
                                        </svg>
                                        <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.9rem' }}>الحاويات:</span>
                                        <span style={{ fontWeight: '700', color: primaryColor }}>{containerNumbers.join(' , ')}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Notes List Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '1rem',
                            paddingBottom: '0.75rem',
                            borderBottom: `2px solid ${primaryColor}20`
                        }}>
                            <h4 style={{
                                margin: 0,
                                color: '#1e293b',
                                fontSize: '1rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <svg style={{ width: '18px', height: '18px', color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                                </svg>
                                جميع الملاحظات
                            </h4>
                            <span style={{
                                backgroundColor: primaryColor,
                                color: '#fff',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '20px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                            }}>
                                {sortedNotes.length}
                            </span>
                        </div>

                        {/* Notes List */}
                        {sortedNotes.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#94a3b8'
                            }}>
                                <svg style={{ width: '64px', height: '64px', opacity: 0.5, margin: '0 auto 1rem' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                </svg>
                                <p style={{ margin: 0, fontSize: '1rem' }}>لا توجد ملاحظات لهذا الملف</p>
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>أضف ملاحظتك الأولى من الأسفل</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {sortedNotes.map((note, index) => (
                                    <div 
                                        key={note.id} 
                                        style={{
                                            backgroundColor: index === 0 ? primaryLight : '#fff',
                                            border: index === 0 ? `2px solid ${primaryColor}` : '1px solid #e2e8f0',
                                            borderRadius: '12px',
                                            padding: '1rem 1.25rem',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {/* Note Header */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '0.75rem',
                                            flexWrap: 'wrap',
                                            gap: '0.5rem'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem'
                                            }}>
                                                {index === 0 && (
                                                    <span style={{
                                                        backgroundColor: primaryColor,
                                                        color: '#fff',
                                                        padding: '0.2rem 0.6rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '700'
                                                    }}>
                                                        الأحدث
                                                    </span>
                                                )}
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.35rem',
                                                    color: '#475569',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600'
                                                }}>
                                                    <svg style={{ width: '16px', height: '16px', color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                    </svg>
                                                    {note.by_user}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.35rem',
                                                    color: '#64748b',
                                                    fontSize: '0.8rem',
                                                    direction: 'ltr'
                                                }}>
                                                    <svg style={{ width: '14px', height: '14px' }} fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                    </svg>
                                                    {formatDateTime(note.created_at)}
                                                </span>
                                                
                                                {/* Edit/Delete — أي مستخدم مصادق يمكنه تعديل/حذف أي ملاحظة */}
                                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                    <button
                                                        onClick={() => {
                                                            setEditingNote(note.id);
                                                            setEditText(note.text);
                                                        }}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            padding: '0.3rem',
                                                            cursor: 'pointer',
                                                            color: primaryColor,
                                                            borderRadius: '4px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = primaryLight}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        title="تعديل"
                                                    >
                                                        <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(note.id)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            padding: '0.3rem',
                                                            cursor: 'pointer',
                                                            color: '#dc3545',
                                                            borderRadius: '4px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        title="حذف"
                                                    >
                                                        <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Note Text or Edit Mode */}
                                        {editingNote === note.id ? (
                                            <div>
                                                <textarea
                                                    value={editText}
                                                    onChange={(e) => setEditText(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        minHeight: '60px',
                                                        padding: '0.75rem',
                                                        borderRadius: '8px',
                                                        border: `1px solid ${primaryColor}`,
                                                        fontSize: '0.95rem',
                                                        fontFamily: 'inherit',
                                                        resize: 'vertical',
                                                        direction: 'rtl',
                                                        outline: 'none',
                                                        marginBottom: '0.5rem'
                                                    }}
                                                />
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <button
                                                        onClick={() => handleEditNote(note.id)}
                                                        style={{
                                                            padding: '0.4rem 1rem',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '600',
                                                            color: '#fff',
                                                            backgroundColor: primaryColor,
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        حفظ
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingNote(null);
                                                            setEditText('');
                                                        }}
                                                        style={{
                                                            padding: '0.4rem 1rem',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '600',
                                                            color: '#666',
                                                            backgroundColor: '#f1f5f9',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        إلغاء
                                                    </button>
                                                </div>
                                            </div>
                                        ) : deleteConfirm === note.id ? (
                                            <div style={{
                                                backgroundColor: '#fee2e2',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                textAlign: 'center'
                                            }}>
                                                <p style={{ margin: '0 0 0.75rem', color: '#991b1b', fontWeight: '600' }}>
                                                    هل أنت متأكد من حذف هذه الملاحظة؟
                                                </p>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        style={{
                                                            padding: '0.4rem 1rem',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '600',
                                                            color: '#fff',
                                                            backgroundColor: '#dc3545',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        نعم، احذف
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(null)}
                                                        style={{
                                                            padding: '0.4rem 1rem',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '600',
                                                            color: '#666',
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        إلغاء
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                color: '#1e293b',
                                                fontSize: '0.95rem',
                                                lineHeight: '1.7',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                {note.text}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add New Note Section - Outside export area */}
                    <div style={{
                        backgroundColor: primaryLight,
                        borderRadius: '12px',
                        padding: '1.25rem',
                        marginTop: '1.5rem',
                        border: `1px solid ${primaryColor}20`
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '1rem',
                            color: primaryColor,
                            fontWeight: '600',
                            fontSize: '0.95rem'
                        }}>
                            <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            إضافة ملاحظة جديدة
                            <span style={{ 
                                fontSize: '0.8rem', 
                                fontWeight: '500', 
                                color: '#666',
                                marginRight: 'auto'
                            }}>
                                ({userData.name})
                            </span>
                        </div>
                        <textarea
                            value={newNoteText}
                            onChange={(e) => {
                                setNewNoteText(e.target.value);
                                if (error) setError('');
                            }}
                            placeholder="اكتب ملاحظتك هنا..."
                            style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '0.875rem',
                                borderRadius: '10px',
                                border: error ? '2px solid #dc3545' : `1px solid ${primaryColor}40`,
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                marginBottom: '0.75rem',
                                direction: 'rtl',
                                outline: 'none',
                                transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => {
                                if (!error) e.target.style.borderColor = primaryColor;
                            }}
                            onBlur={(e) => {
                                if (!error) e.target.style.borderColor = `${primaryColor}40`;
                            }}
                        />
                        {error && (
                            <p style={{ 
                                color: '#dc3545', 
                                fontSize: '0.85rem', 
                                margin: '0 0 0.75rem 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.3rem'
                            }}>
                                <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </p>
                        )}
                        <button
                            onClick={handleAddNote}
                            disabled={isSubmitting}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                padding: '0.65rem 1.5rem',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#fff',
                                backgroundColor: primaryColor,
                                border: 'none',
                                borderRadius: '10px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.7 : 1,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) e.currentTarget.style.backgroundColor = '#022d61';
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) e.currentTarget.style.backgroundColor = primaryColor;
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid #fff',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }}></div>
                                    جاري الإضافة...
                                </>
                            ) : (
                                <>
                                    <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    إضافة الملاحظة
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}>
                    <button 
                        onClick={handleClose}
                        style={{
                            padding: '0.6rem 1.5rem',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#64748b',
                            backgroundColor: '#f1f5f9',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e2e8f0';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }}
                    >
                        إغلاق
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default NotesModal;
